import {
  createDeferredPromise,
  createSignalState,
  createSingletonPromise,
  dataSignalFlow,
  openLoading,
  retryService,
  SignalState,
  sleep,
  toast,
  DeferredPromise,
  createCachedPromise,
} from '@pst/utils';
import { isProduction } from './env';

const SETTINGS_APPID = 331;
const SETTINGS_REPO = 'P6SDK';

interface SettingRes<T> {
  data: {
    settings: T;
  };
}

type RemoteSettingKeys<T> = readonly (keyof T)[];

function pickSettings<T, K extends keyof T>(settings: T, keys?: readonly K[]) {
  if (!keys?.length) {
    return settings;
  }

  return keys.reduce(
    (result, key) => {
      if (Object.prototype.hasOwnProperty.call(settings, key)) {
        result[key] = settings[key];
      }

      return result;
    },
    {} as Pick<T, K>,
  );
}

function isKeys<T, K extends keyof T>(keys: K | readonly K[] | undefined): keys is readonly K[] {
  return Array.isArray(keys);
}

let cache: SettingRes<unknown> | undefined = undefined;
async function fetchRemoteSettingsData<T>(): Promise<T> {
  if (cache) {
    return (cache as SettingRes<T>).data.settings;
  }

  const api = `/service/settings/v3?app=${SETTINGS_APPID}&caller_name=${SETTINGS_REPO}`;

  const res = await fetch(`${location.origin}${api}${isProduction ? '' : '&env=ppe'}`, {
    referrerPolicy: 'unsafe-url',
  });

  const data = (await res.json()) as SettingRes<T>;

  if (data.data.settings) {
    cache = data;
  }

  return data.data.settings;
}

const getRemoteSettingsData = createSingletonPromise(retryService(fetchRemoteSettingsData as any)) as <
  T,
>() => Promise<T>;

async function fetchRemoteSettings<T>(): Promise<T>;
async function fetchRemoteSettings<T, K extends keyof T>(key: K): Promise<T[K]>;
async function fetchRemoteSettings<T, K extends keyof T>(keys: readonly K[]): Promise<Pick<T, K>>;
async function fetchRemoteSettings<T, K extends keyof T>(
  keys?: K | readonly K[],
): Promise<T | T[K] | Pick<T, K>> {
  const settings = await getRemoteSettingsData<T>();

  if (isKeys<T, K>(keys)) {
    return pickSettings(settings, keys);
  }

  if (keys !== undefined) {
    return settings[keys];
  }

  return settings;
}

interface FetchRemoteSettings {
  <T>(): Promise<T>;
  <T, K extends keyof T>(key: K): Promise<T[K]>;
  <T, K extends keyof T>(keys: readonly K[]): Promise<Pick<T, K>>;
}

/**
 * 获取 settings 配置
 */
export const getRemoteSettings = fetchRemoteSettings as FetchRemoteSettings;

export class RemoteSettingsModule<T> {
  data = createSignalState<T>({ defaultValue: {} as T });
  queue: DeferredPromise<unknown> | undefined = createDeferredPromise();
  keys?: RemoteSettingKeys<T>;
  cacheOptions?: {
    key: string;
    ttl?: number;
    type?: 'sessionStorage' | 'localStorage';
  };

  constructor(options?: {
    autoInit?: boolean;
    keys?: RemoteSettingKeys<T>;
    cache?: {
      key: string;
      ttl?: number;
      type?: 'sessionStorage' | 'localStorage';
    };
  }) {
    this.keys = options?.keys;
    this.cacheOptions = options?.cache;
    if (options?.autoInit ?? true) {
      this.init();
    }
  }

  async init() {
    const fetchSettings = getRemoteSettings as (keys?: RemoteSettingKeys<T>) => Promise<T>;
    let fetcher = () => fetchSettings(this.keys?.length ? this.keys : undefined);
    if (this.cacheOptions) {
      fetcher = createCachedPromise(fetcher, this.cacheOptions);
    }
    await dataSignalFlow(this.data, fetcher);
    this.queue?.resolve(undefined);
  }

  getData() {
    return this.data.data.value;
  }

  async clickProtection(
    onClick: () => void | Promise<void>,
    options?: {
      errorHandler?: () => void;
      loadingHandler?: () => void;
    },
  ) {
    const { errorHandler, loadingHandler } = options || {};
    if (this.data.error.value) {
      if (errorHandler) {
        errorHandler();
      } else {
        toast('fetch remote settings failed, please refresh the page');
      }
      return;
    }

    if (this.data.loading.value) {
      if (loadingHandler) {
        loadingHandler();
      } else {
        const loading = openLoading();
        if (this.queue) {
          await this.queue.promise;
        }
        loading();
        this.queue = undefined;
      }
    }

    await onClick();
  }
}
